import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { buildPostgrestOrIlikeFilter } from '@/utils/postgrestFilters';
import { API_CONFIG } from '@/config/api';
import { reportsRegistry } from '@/reports/registry';
import type { WorkflowCart } from '@/services/cart/types';
import type { IARRunSummary } from '@/types/iar';
import type { MaintenanceRequestLogViewRow } from '@/services/maintenance/MaintenanceRequestLogsService';
import type { TransfersShippingLogViewRow } from '@/services/transfers-shipping/TransfersShippingLogsService';
import type { BarcodeLabelRow } from '@/services/BarcodeService';

/* ---------- unified result shape ---------- */

export interface GlobalSearchResult {
  id: string;
  /** Broad category used for chip labels and navigation routing. */
  category: string;
  /** Human-readable display name shown in the dropdown. */
  display_name: string;
  /** Secondary identifier shown beneath the name (optional). */
  secondary?: string;
  /** Where to navigate when the result is clicked. */
  route: string;
}

/* ---------- entity RPC result ---------- */

interface EntityRpcResult {
  id: string;
  entity_type: string;
  entity_identifier: string | null;
  display_name: string;
  entity_data: Record<string, unknown>;
  status: string;
  relevance_score: number;
}

/* ---------- entity → route helpers ---------- */

function entityRoute(r: EntityRpcResult): string {
  if (r.entity_type === 'transaction') return `/transactions?txnId=${encodeURIComponent(r.id)}`;
  if (r.entity_type === 'asset_transaction') {
    const txnId = (r.entity_data?.transaction_id as string | undefined) ?? r.id;
    return `/transactions?txnId=${encodeURIComponent(txnId)}`;
  }
  if (r.entity_type === 'order') return `/purchase-requisitions`;
  if (r.entity_type === 'loss_record') return `/asset-loss-management`;
  if (r.entity_type === 'bulk_inventory_bin') {
    const ws = r.entity_data?.workflow_status as string | undefined;
    return ws === 'pending_disposal' ? '/bulk-dispose' : '/bulk-inventory-bin';
  }
  if (r.entity_type === 'hand_receipt') {
    const personId = r.entity_data?.person_id as string | undefined;
    const tab = r.entity_data?.valid_to ? 'temporary-hand-receipt' : 'permanent-hand-receipt';
    return personId ? `/person/${personId}?tab=${tab}` : '/customer-service';
  }
  if (r.entity_type === 'person_sizing') {
    const personId = r.entity_data?.person_id as string | undefined;
    return personId ? `/person/${personId}` : '/person';
  }
  return `/${r.entity_type}/${r.id}`;
}

const ENTITY_CATEGORY: Record<string, string> = {
  asset: 'Asset',
  person: 'Person',
  catalog: 'Catalog',
  location: 'Location',
  hand_receipt: 'Hand Receipt',
  ie_kit: 'IE Kit', // overridden at runtime via useGlobalSearch
  transaction: 'Transaction',
  asset_transaction: 'Asset Transaction',
  maintenance_receipt: 'Maintenance',
  person_sizing: 'Person Sizing',
  asset_status_type: 'Status Type',
  order: 'Purchase Requisition',
  loss_record: 'Loss Report',
  bulk_inventory_bin: 'Bulk Inventory',
};

/* ---------- formatters for non-entity sources ---------- */

function formatIARRunNumber(run: IARRunSummary): string {
  return run.runNumber || `IAR-${run.runId.slice(0, 8)}`;
}

const WORKFLOW_LABELS: Record<string, string> = {
  issue: 'Issue',
  exchange: 'Exchange',
  turn_in: 'Turn-In',
  'maintenance-send': 'Maintenance Send',
  'maintenance-receive': 'Maintenance Receive',
  transfer: 'Transfer',
  shipment: 'Shipment',
};

/** Entity types excluded from global search results (no routable detail page). */
const EXCLUDED_ENTITY_TYPES = new Set([
  'document',
  'document_generation_payload',
  'asset_status_type',
  'order_line_item',
  'order_receipt',
  'maintenance_request_line_item',
  'transfer_shipping_log',
  'transfer_shipping_log_item',
  'ie_kit_item',
  'individual_state',
  'workflow_cart',
  'asset_reservation',
  'size_allocation_cart',
  'feedback',
  'person_ie_kit',
]);

/* ---------- parallel fetch functions ---------- */

async function searchTransactionLog(query: string): Promise<GlobalSearchResult[]> {
  interface TransactionLogSearchEntry {
    id: string;
    documentNumber: string;
    searchableDocumentNumbers?: string;
    transactionType: string;
    memberName: string;
    performedByName: string;
    dateProcessed: string;
    sourceWorkflow?: string;
    logFormNumber?: string;
    documentType?: string | null;
    notes?: string;
  }
  const response = await httpClient.get<{ transactions: TransactionLogSearchEntry[] }>(
    `${API_CONFIG.ACTIONS_API_BASE_PATH}/transactions/log?limit=200`,
  );
  const transactions = response?.transactions ?? [];
  return transactions
    .filter((t) => {
      const haystack = [
        t.documentNumber,
        t.searchableDocumentNumbers,
        t.transactionType,
        t.memberName,
        t.performedByName,
        t.sourceWorkflow,
        t.logFormNumber,
        t.documentType,
        t.notes,
      ].filter(Boolean).join(' ');
      return multiWordMatch(haystack, query);
    })
    .slice(0, 10)
    .map((t) => ({
      id: t.id,
      category: 'Transaction',
      display_name: t.documentNumber || `Transaction ${t.id.slice(0, 8)}`,
      secondary: `${t.transactionType} — ${t.memberName}`,
      route: `/transactions?txnId=${encodeURIComponent(t.id)}`,
    }));
}

async function searchEntities(query: string, limit: number): Promise<GlobalSearchResult[]> {
  const params = new URLSearchParams({
    is_current: 'eq.true',
    order: 'display_name.asc',
    limit: String(limit),
    or: buildPostgrestOrIlikeFilter(['display_name', 'entity_identifier'], query),
  });
  const results = await httpClient.get<EntityRpcResult[]>(`/api/v1/entities?${params}`);
  return (Array.isArray(results) ? results : [])
    .filter((r) => !EXCLUDED_ENTITY_TYPES.has(r.entity_type))
    .map((r) => ({
    id: r.id,
    category: ENTITY_CATEGORY[r.entity_type] ?? r.entity_type,
    display_name: r.display_name,
    secondary: r.entity_identifier ?? undefined,
    route: entityRoute(r),
  }));
}

/** Entity types that link to a person via entity_data->person_id */
const PERSON_LINKED_TYPES = ['loss_record', 'hand_receipt'] as const;

/**
 * Search for entities linked to a person by name.
 * First finds persons matching the query, then fetches related entities by person_id.
 */
async function searchPersonLinkedEntities(query: string): Promise<GlobalSearchResult[]> {
  // Find persons matching the query
  const personParams = new URLSearchParams({
    is_current: 'eq.true',
    entity_type: 'eq.person',
    limit: '10',
    select: 'id,display_name',
    or: buildPostgrestOrIlikeFilter(['display_name', 'entity_identifier'], query),
  });
  const persons = await httpClient.get<{ id: string; display_name: string }[]>(
    `/api/v1/entities?${personParams}`,
  );
  if (!Array.isArray(persons) || persons.length === 0) return [];

  const personIds = persons.map((p) => p.id);
  const personNameMap = new Map(persons.map((p) => [p.id, p.display_name]));

  // Fetch related entities for those person IDs
  const linkedParams = new URLSearchParams({
    is_current: 'eq.true',
    limit: '50',
    or: `(${personIds.map((id) => `entity_data->>person_id.eq.${id}`).join(',')})`,
    entity_type: `in.(${PERSON_LINKED_TYPES.join(',')})`,
  });
  const linked = await httpClient.get<EntityRpcResult[]>(`/api/v1/entities?${linkedParams}`);
  if (!Array.isArray(linked)) return [];

  return linked
    .filter((r) => !EXCLUDED_ENTITY_TYPES.has(r.entity_type))
    .map((r) => {
      const personId = r.entity_data?.person_id as string | undefined;
      const personName = personId ? personNameMap.get(personId) : undefined;
      return {
        id: r.id,
        category: ENTITY_CATEGORY[r.entity_type] ?? r.entity_type,
        display_name: r.display_name,
        secondary: [r.entity_identifier, personName].filter(Boolean).join(' — ') || undefined,
        route: entityRoute(r),
      };
    });
}

async function searchCarts(query: string): Promise<GlobalSearchResult[]> {
  const carts = await httpClient.get<WorkflowCart[]>('/api/v1/workflow_carts?select=*');
  return carts
    .filter((c) => {
      const text = [
        c.customer_display_name,
        c.workflow_type,
        WORKFLOW_LABELS[c.workflow_type],
        c.document_number,
      ]
        .filter(Boolean)
        .join(' ');
      return multiWordMatch(text, query);
    })
    .map((c) => ({
      id: c.id,
      category: 'Saved Cart',
      display_name: c.customer_display_name ?? 'Unnamed Cart',
      secondary: WORKFLOW_LABELS[c.workflow_type] ?? c.workflow_type,
      route: '/property-book/reserved-stock',
    }));
}

async function searchIARRuns(query: string): Promise<GlobalSearchResult[]> {
  const response = await httpClient.get<{ runs: IARRunSummary[] } | IARRunSummary[]>(
    `${API_CONFIG.ACTIONS_API_BASE_PATH}/iar/runs`,
  );
  const runs = Array.isArray(response) ? response : response?.runs ?? [];
  return runs
    .filter((r) => {
      const text = [formatIARRunNumber(r), r.status, r.runDate].filter(Boolean).join(' ');
      return multiWordMatch(text, query);
    })
    .map((r) => ({
      id: r.runId,
      category: 'IAR Run',
      display_name: formatIARRunNumber(r),
      secondary: r.status,
      route: `/iar/${r.runId}`,
    }));
}

async function searchMaintenanceRequests(query: string): Promise<GlobalSearchResult[]> {
  const logs = await httpClient.get<MaintenanceRequestLogViewRow[]>(
    '/api/v1/maintenance_request_logs_view?order=date_sent.desc&limit=200',
  );
  return logs
    .filter((l) => {
      const text = [l.document_number, l.asset_names_agg, l.status_label, l.lins_agg, l.nsns_agg]
        .filter(Boolean)
        .join(' ');
      return multiWordMatch(text, query);
    })
    .map((l) => ({
      id: l.id,
      category: 'Maintenance Request',
      display_name: l.document_number ?? l.display_name ?? 'Maintenance Request',
      secondary: `${l.status_label} — ${l.total_quantity} items`,
      route: '/maintenance-request',
    }));
}

async function searchTransfers(query: string): Promise<GlobalSearchResult[]> {
  const logs = await httpClient.get<TransfersShippingLogViewRow[]>(
    '/api/v1/transfers_shipping_logs_view?order=date_sent.desc&limit=200',
  );
  return logs
    .filter((l) => {
      const text = [l.transfer_type, l.display_name, l.entity_identifier]
        .filter(Boolean)
        .join(' ');
      return multiWordMatch(text, query);
    })
    .map((l) => ({
      id: l.id,
      category: 'Transfer / Shipment',
      display_name: l.display_name ?? l.entity_identifier ?? 'Transfer',
      secondary: l.transfer_type ?? undefined,
      route: '/transfers-shipping',
    }));
}

async function searchBarcodes(query: string): Promise<GlobalSearchResult[]> {
  const mappings = await httpClient.get<BarcodeLabelRow[]>(
    '/api/v1/barcode_mappings?order=created_at.desc&limit=500',
  );
  return mappings
    .filter((b) => {
      const text = [
        b.barcode_value,
        b.catalog_name,
        b.lin,
        b.nsn,
        b.location_name,
        b.location_path,
      ]
        .filter(Boolean)
        .join(' ');
      return multiWordMatch(text, query);
    })
    .map((b) => ({
      id: b.id,
      category: 'Barcode Label',
      display_name: b.catalog_name || b.barcode_value,
      secondary: b.location_name ? `${b.barcode_value} — ${b.location_name}` : b.barcode_value,
      route: '/barcode-labels',
    }));
}

function searchReports(query: string): GlobalSearchResult[] {
  return reportsRegistry
    .filter((r) => multiWordMatch(`${r.name} ${r.description}`, query))
    .map((r) => ({
      id: r.id,
      category: 'Report',
      display_name: r.name,
      secondary: r.description,
      route: '/reports',
    }));
}

/* ---------- main hook ---------- */

/**
 * Unified global search across all system objects:
 * entities (via RPC full-text search), saved carts, IAR runs,
 * maintenance requests, transfers, barcode labels, and reports.
 */
export function useGlobalSearch(rawQuery: string, limit = 25) {
  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, 300);
  const { label: ieKitLabel } = useIeKitLabels('global_search');
  const { individualIdPrefix } = usePersonLabels('global_search');

  const enabled = debouncedQuery.length > 0;

  return useQuery<GlobalSearchResult[]>({
    queryKey: ['globalSearch', debouncedQuery, limit, ieKitLabel, individualIdPrefix],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      // Fire all searches in parallel — entity RPC + non-entity sources
      const [entities, personLinked, carts, iarRuns, maintenance, transfers, barcodes, transactionLogs] = await Promise.all([
        searchEntities(debouncedQuery, limit).catch(() => [] as GlobalSearchResult[]),
        searchPersonLinkedEntities(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
        searchCarts(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
        searchIARRuns(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
        searchMaintenanceRequests(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
        searchTransfers(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
        searchBarcodes(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
        searchTransactionLog(debouncedQuery).catch(() => [] as GlobalSearchResult[]),
      ]);

      const reports = searchReports(debouncedQuery);

      // Deduplicate by id (entity search may overlap with person-linked results)
      const seen = new Set<string>();
      const all = [...entities, ...personLinked, ...transactionLogs, ...carts, ...iarRuns, ...maintenance, ...transfers, ...barcodes, ...reports];
      return all
        .filter((r) => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        })
        .map((r) => {
          let result = r;
          if (r.category === 'IE Kit' && ieKitLabel !== 'IE Kit') {
            result = { ...result, category: ieKitLabel };
          }
          if (r.category === 'Person' && result.secondary) {
            result = { ...result, secondary: result.secondary.replace(/^INDV-/, individualIdPrefix) };
          }
          return result;
        });
    },
  });
}
